import { useState } from 'react';
import { useNavigate, useLoaderData, Form, useActionData, LoaderFunctionArgs, ActionFunctionArgs } from 'react-router';
import { apiClient } from '../../../lib/api-client';
import { requireUser } from '../../../lib/session.server';

interface NotificationPreferences {
  emailNotifications: {
    newJobs: boolean;
    applicationUpdates: boolean;
    weeklyDigest: boolean;
    productUpdates: boolean;
    marketingEmails: boolean;
  };
  pushNotifications: {
    enabled: boolean;
    newJobs: boolean;
    applicationUpdates: boolean;
    messages: boolean;
  };
  smsNotifications: {
    enabled: boolean;
    urgentUpdates: boolean;
  };
}

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await requireUser(request);
  
  try {
    const preferences = await apiClient.get('/account/notifications');
    return { user, preferences };
  } catch (error) {
    return { 
      user, 
      preferences: {
        emailNotifications: {
          newJobs: true,
          applicationUpdates: true,
          weeklyDigest: true,
          productUpdates: false,
          marketingEmails: false
        },
        pushNotifications: {
          enabled: false,
          newJobs: false,
          applicationUpdates: false,
          messages: false
        },
        smsNotifications: {
          enabled: false,
          urgentUpdates: false
        }
      }
    };
  }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  
  const preferences = {
    emailNotifications: {
      newJobs: formData.get('email_newJobs') === 'on',
      applicationUpdates: formData.get('email_applicationUpdates') === 'on',
      weeklyDigest: formData.get('email_weeklyDigest') === 'on',
      productUpdates: formData.get('email_productUpdates') === 'on',
      marketingEmails: formData.get('email_marketingEmails') === 'on'
    },
    pushNotifications: {
      enabled: formData.get('push_enabled') === 'on',
      newJobs: formData.get('push_newJobs') === 'on',
      applicationUpdates: formData.get('push_applicationUpdates') === 'on',
      messages: formData.get('push_messages') === 'on'
    },
    smsNotifications: {
      enabled: formData.get('sms_enabled') === 'on',
      urgentUpdates: formData.get('sms_urgentUpdates') === 'on'
    }
  };
  
  try {
    await apiClient.put('/account/notifications', preferences);
    return { success: true, message: 'Notification preferences updated successfully' };
  } catch (error: any) {
    return { error: error.message || 'Failed to update preferences' };
  }
}

export function SettingsNotificationsPage() {
  const { preferences } = useLoaderData() as any;
  const actionData = useActionData() as any;
  const navigate = useNavigate();
  
  const [prefs, setPrefs] = useState<NotificationPreferences>(preferences);

  const handleCheckboxChange = (category: string, field: string) => {
    setPrefs(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof NotificationPreferences],
        [field]: !(prev[category as keyof NotificationPreferences] as any)[field]
      }
    }));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Notification Settings</h1>
        <p className="text-gray-600">Choose how you want to be notified about updates</p>
      </div>

      {actionData?.success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
          {actionData.message}
        </div>
      )}

      {actionData?.error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {actionData.error}
        </div>
      )}

      <Form method="post" className="space-y-6">
        {/* Email Notifications */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Email Notifications</h2>
          
          <div className="space-y-3">
            <label className="flex items-center justify-between">
              <div>
                <span className="font-medium">New Job Matches</span>
                <p className="text-sm text-gray-600">Get notified when new jobs match your preferences</p>
              </div>
              <input
                type="checkbox"
                name="email_newJobs"
                checked={prefs.emailNotifications.newJobs}
                onChange={() => handleCheckboxChange('emailNotifications', 'newJobs')}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </label>
            
            <label className="flex items-center justify-between">
              <div>
                <span className="font-medium">Application Updates</span>
                <p className="text-sm text-gray-600">Receive updates about your job applications</p>
              </div>
              <input
                type="checkbox"
                name="email_applicationUpdates"
                checked={prefs.emailNotifications.applicationUpdates}
                onChange={() => handleCheckboxChange('emailNotifications', 'applicationUpdates')}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </label>
            
            <label className="flex items-center justify-between">
              <div>
                <span className="font-medium">Weekly Digest</span>
                <p className="text-sm text-gray-600">Summary of your job search activity and recommendations</p>
              </div>
              <input
                type="checkbox"
                name="email_weeklyDigest"
                checked={prefs.emailNotifications.weeklyDigest}
                onChange={() => handleCheckboxChange('emailNotifications', 'weeklyDigest')}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </label>
            
            <label className="flex items-center justify-between">
              <div>
                <span className="font-medium">Product Updates</span>
                <p className="text-sm text-gray-600">New features and improvements</p>
              </div>
              <input
                type="checkbox"
                name="email_productUpdates"
                checked={prefs.emailNotifications.productUpdates}
                onChange={() => handleCheckboxChange('emailNotifications', 'productUpdates')}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </label>
            
            <label className="flex items-center justify-between">
              <div>
                <span className="font-medium">Marketing Emails</span>
                <p className="text-sm text-gray-600">Tips, resources, and promotional content</p>
              </div>
              <input
                type="checkbox"
                name="email_marketingEmails"
                checked={prefs.emailNotifications.marketingEmails}
                onChange={() => handleCheckboxChange('emailNotifications', 'marketingEmails')}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </label>
          </div>
        </div>

        {/* Push Notifications */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Push Notifications</h2>
          
          <div className="space-y-3">
            <label className="flex items-center justify-between">
              <div>
                <span className="font-medium">Enable Push Notifications</span>
                <p className="text-sm text-gray-600">Receive notifications in your browser</p>
              </div>
              <input
                type="checkbox"
                name="push_enabled"
                checked={prefs.pushNotifications.enabled}
                onChange={() => handleCheckboxChange('pushNotifications', 'enabled')}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </label>
            
            {prefs.pushNotifications.enabled && (
              <>
                <label className="flex items-center justify-between pl-6">
                  <div>
                    <span className="font-medium">New Job Matches</span>
                    <p className="text-sm text-gray-600">Instant alerts for matching jobs</p>
                  </div>
                  <input
                    type="checkbox"
                    name="push_newJobs"
                    checked={prefs.pushNotifications.newJobs}
                    onChange={() => handleCheckboxChange('pushNotifications', 'newJobs')}
                    className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </label>
                
                <label className="flex items-center justify-between pl-6">
                  <div>
                    <span className="font-medium">Application Updates</span>
                    <p className="text-sm text-gray-600">Status changes on your applications</p>
                  </div>
                  <input
                    type="checkbox"
                    name="push_applicationUpdates"
                    checked={prefs.pushNotifications.applicationUpdates}
                    onChange={() => handleCheckboxChange('pushNotifications', 'applicationUpdates')}
                    className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </label>
                
                <label className="flex items-center justify-between pl-6">
                  <div>
                    <span className="font-medium">Messages</span>
                    <p className="text-sm text-gray-600">Direct messages from employers</p>
                  </div>
                  <input
                    type="checkbox"
                    name="push_messages"
                    checked={prefs.pushNotifications.messages}
                    onChange={() => handleCheckboxChange('pushNotifications', 'messages')}
                    className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </label>
              </>
            )}
          </div>
        </div>

        {/* SMS Notifications */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">SMS Notifications</h2>
          
          <div className="space-y-3">
            <label className="flex items-center justify-between">
              <div>
                <span className="font-medium">Enable SMS Notifications</span>
                <p className="text-sm text-gray-600">Receive text messages for important updates</p>
              </div>
              <input
                type="checkbox"
                name="sms_enabled"
                checked={prefs.smsNotifications.enabled}
                onChange={() => handleCheckboxChange('smsNotifications', 'enabled')}
                className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
            </label>
            
            {prefs.smsNotifications.enabled && (
              <label className="flex items-center justify-between pl-6">
                <div>
                  <span className="font-medium">Urgent Updates Only</span>
                  <p className="text-sm text-gray-600">Interview requests and time-sensitive updates</p>
                </div>
                <input
                  type="checkbox"
                  name="sms_urgentUpdates"
                  checked={prefs.smsNotifications.urgentUpdates}
                  onChange={() => handleCheckboxChange('smsNotifications', 'urgentUpdates')}
                  className="h-5 w-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </label>
            )}
          </div>
        </div>

        {/* Quiet Hours */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold mb-4">Quiet Hours</h2>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Set times when you don't want to receive push or SMS notifications
            </p>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  name="quietHoursStart"
                  defaultValue="22:00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  name="quietHoursEnd"
                  defaultValue="08:00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/settings')}
            className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            Save Preferences
          </button>
        </div>
      </Form>
    </div>
  );
}